import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { SERVICES } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const QuoteDialog = ({ open, onOpenChange, defaultService }) => {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: defaultService || "",
    pipeline_length: "",
    location: "",
    timeline: "",
    message: "",
  });

  React.useEffect(() => {
    if (defaultService) setForm((f) => ({ ...f, service: defaultService }));
  }, [defaultService]);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service) {
      toast.error("Please fill name, email, phone and service.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quote`, form);
      toast.success("Quote request received. Our team will reach out within 1 business day.");
      onOpenChange(false);
      setForm({
        name: "",
        email: "",
        phone: "",
        company: "",
        service: defaultService || "",
        pipeline_length: "",
        location: "",
        timeline: "",
        message: "",
      });
    } catch (err) {
      toast.error("Submission failed. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl rounded-none border-slate-200"
        data-testid="quote-dialog"
      >
        <DialogHeader>
          <p className="overline">Request a Quote</p>
          <DialogTitle className="font-heading text-2xl md:text-3xl text-brand-blue">
            Tell us about your project
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            Share a few details and our engineering team will respond within one business day.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <div>
            <Label htmlFor="q-name">Full Name *</Label>
            <Input id="q-name" data-testid="quote-name" value={form.name} onChange={onChange("name")} className="rounded-none mt-1.5" required />
          </div>
          <div>
            <Label htmlFor="q-email">Email *</Label>
            <Input id="q-email" type="email" data-testid="quote-email" value={form.email} onChange={onChange("email")} className="rounded-none mt-1.5" required />
          </div>
          <div>
            <Label htmlFor="q-phone">Phone *</Label>
            <Input id="q-phone" data-testid="quote-phone" value={form.phone} onChange={onChange("phone")} className="rounded-none mt-1.5" required />
          </div>
          <div>
            <Label htmlFor="q-company">Company</Label>
            <Input id="q-company" data-testid="quote-company" value={form.company} onChange={onChange("company")} className="rounded-none mt-1.5" />
          </div>
          <div className="md:col-span-2">
            <Label>Service *</Label>
            <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
              <SelectTrigger className="rounded-none mt-1.5" data-testid="quote-service">
                <SelectValue placeholder="Select a service" />
              </SelectTrigger>
              <SelectContent>
                {SERVICES.map((s) => (
                  <SelectItem key={s.slug} value={s.title} data-testid={`quote-service-${s.slug}`}>
                    {s.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="q-length">Pipeline Length</Label>
            <Input id="q-length" data-testid="quote-length" placeholder="e.g. 25 km" value={form.pipeline_length} onChange={onChange("pipeline_length")} className="rounded-none mt-1.5" />
          </div>
          <div>
            <Label htmlFor="q-location">Project Location</Label>
            <Input id="q-location" data-testid="quote-location" placeholder="City, State" value={form.location} onChange={onChange("location")} className="rounded-none mt-1.5" />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="q-timeline">Timeline</Label>
            <Input id="q-timeline" data-testid="quote-timeline" placeholder="e.g. Q1 2026" value={form.timeline} onChange={onChange("timeline")} className="rounded-none mt-1.5" />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="q-message">Project Details</Label>
            <Textarea id="q-message" data-testid="quote-message" rows={4} value={form.message} onChange={onChange("message")} className="rounded-none mt-1.5" />
          </div>
          <div className="md:col-span-2 flex flex-col sm:flex-row gap-3 justify-end mt-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="px-5 py-3 text-sm font-semibold border border-slate-300 hover:bg-slate-50"
              data-testid="quote-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              data-testid="quote-submit"
              className="px-6 py-3 text-sm font-semibold bg-brand-orange text-white hover:bg-orange-600 disabled:opacity-60 btn-lift"
            >
              {submitting ? "Submitting…" : "Submit Request"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
